import React, { useCallback, useEffect, useState } from 'react';

import DateFnsUtils from '@date-io/date-fns';

import { makeStyles }  from '@material-ui/core/styles';
import Button          from '@material-ui/core/Button';
import Dialog          from '@material-ui/core/Dialog';
import DialogActions   from '@material-ui/core/DialogActions';
import DialogContent   from '@material-ui/core/DialogContent';
import DialogTitle     from '@material-ui/core/DialogTitle';
import Table           from '@material-ui/core/Table';
import TableBody       from '@material-ui/core/TableBody';
import TableCell       from '@material-ui/core/TableCell';
import TableContainer  from '@material-ui/core/TableContainer';
import TableHead       from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow        from '@material-ui/core/TableRow';
import TextField       from '@material-ui/core/TextField';
import Typography      from '@material-ui/core/Typography';

import LabelOutlinedIcon from '@material-ui/icons/LabelOutlined';

import { KeyboardDateTimePicker }  from '@material-ui/pickers';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import { date }          from '../util';
import NewNote, { note } from './NewNote';

const style = {
  newUsedTicket: makeStyles((theme) => ({
    dialogContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(4),
    },
  })),
  usableTickets: makeStyles((theme) => ({
    counts: {
      paddingBottom: theme.spacing(4),
      paddingLeft: theme.spacing(2),
    },
    ticket: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
      whiteSpace: 'nowrap',
    },
    title: {
      paddingBottom: theme.spacing(2),
    },
  })),
}

const usedTicket = (rowid = -1, note = '', usable = true) => {
  return {
    note,
    rowid,
    usable,
    usedAt: Date.now(),
  };
}

function NewUsedTicket(props) {
  const classes = style.newUsedTicket();

  return (
    <Dialog fullWidth open={props.newUsedTicket.rowid > -1}>
      <DialogTitle>
        {props.newUsedTicket.usable ? '사용' : '만료'}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDateTimePicker format="yyyy/MM/dd hh:mm a"
            label={props.newUsedTicket.usable ? '사용일' : '만료일'}
            onChange={(datetime) => {
              props.setNewUsedTicket({...props.newUsedTicket, usedAt: datetime.getTime()});
            }}
            value={new Date(props.newUsedTicket.usedAt)}
          />
        </MuiPickersUtilsProvider>

        <TextField fullWidth
          label={'비고'}
          multiline rows={4}
          onChange={(event) => { props.setNewUsedTicket({...props.newUsedTicket, note: event.target.value}); }}
          value={props.newUsedTicket.note}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={(event) => { props.setNewUsedTicket(props.usedTicket()); }}>
          {'취소'}
        </Button>
        <Button color="primary" onClick={(event) => { props.updateTicket(); }}>
          {'확인'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function UsableTickets(props) {
  const classes = style.usableTickets();

  const { request, setSnackbarMessage } = props.context.getStates();
  const [ count, setCount ] = useState(0);
  const [ newNote, setNewNote ] = useState(note());
  const [ newUsedTicket, setNewUsedTicket ] = useState(usedTicket());
  const [ page, setPage ] = useState(0);
  const [ usableTickets, setUsableTickets ] = useState([]);
  const [ usableTicketsCounted, setUsableTicketsCounted ] = useState([]);

  const fetchUsableTickets = useCallback((page) => {
    request.postSecurely('/attender/issued/usable', {
      attender: props.attender.rowid,
      page,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        if ('counts' in response) {
          setUsableTicketsCounted(response.counts);

          var newCount = 0;
          for (const _counted of response.counts) {
            newCount += _counted['COUNT(*)'];
          }
          setCount(newCount);
        }
        if ('tickets' in response) {
          setUsableTickets(response.tickets);
        }
      }
    });
  }, [request, props.attender.rowid]);

  const updateNote = () => {
    request.postSecurely('/attender/issued/update', {
      ...newNote,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        setNewNote(note());
        props.setRevision(props.revision + 1);
      }
    });
  }

  const updateTicket = (rowid) => {
    request.postSecurely('/attender/issued/update', {
      ...newUsedTicket,
    }, (response) => {
      if ('error' in response) {
        console.error(response);
      } else {
        setNewUsedTicket(usedTicket());
        props.setRevision(props.revision + 1);
        setSnackbarMessage('티켓을 사용하였습니다.');
      }
    });
  }

  useEffect(() => {
    fetchUsableTickets(0);
  }, [
    fetchUsableTickets,
    props.revision,
  ]);

  return (
    <div>
      <NewUsedTicket newUsedTicket={newUsedTicket}
        setNewUsedTicket={setNewUsedTicket}
        updateTicket={updateTicket}
        usedTicket={usedTicket}
      />

      <NewNote newNote={newNote}
        note={note}
        setNewNote={setNewNote}
        updateNote={updateNote}
      />

      <Typography variant="h6" className={classes.title}>{'사용 가능한 티켓'}</Typography>

      <div className={classes.counts}>
        {usableTicketsCounted.map((ticket, index) => (
          <div key={index} className={classes.ticket}>
            <LabelOutlinedIcon />
            <span><b>{ticket['ticket.name']}</b> &times; {ticket['COUNT(*)']}회</span>
          </div>
        ))}
      </div>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {['티켓 ID', '발급일', '만료일', '이용권', '비고', ''].map((column, i) => (
                <TableCell key={i}
                  style={{whiteSpace: 'nowrap'}}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {usableTickets.map((ticket) => (
                <TableRow hover tabIndex={-1} key={ticket['issued.rowid']}>
                  <TableCell>
                    {ticket['issued.rowid']}
                  </TableCell>
                  <TableCell>
                      {date.withoutTime(ticket['issued.date'])}
                  </TableCell>
                  <TableCell>
                      {date.withoutTime(ticket['ticket.active_until'])}
                  </TableCell>
                  <TableCell>
                    <div className={classes.ticket}>
                      <LabelOutlinedIcon />
                      <b>{ticket['ticket.name']}</b>
                      <Button color="primary"
                        onClick={(event) => {
                          setNewUsedTicket(usedTicket(ticket['issued.rowid'], ticket['issued.note'], true));
                        }}
                      >{'사용'}</Button>
                      <Button color="primary"
                        onClick={(event) => {
                          setNewUsedTicket(usedTicket(ticket['issued.rowid'], ticket['issued.note'], false));
                        }}
                      >{'만료'}</Button>
                    </div>
                  </TableCell>
                  <TableCell>
                      {ticket['issued.note'].split('\n').map((line, index) => (
                        <span key={index}>{line}<br/></span>
                      ))}
                  </TableCell>
                  <TableCell align={'right'}>
                    <Button
                      style={{padding: 0,}}
                      onClick={(event) => { setNewNote(note(ticket['issued.rowid'], ticket['issued.note'])); }}
                    >{'수정'}</Button>
                  </TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={5}
        page={page}
        onPageChange={(event, newPage) => { fetchUsableTickets(newPage); setPage(newPage); }}
      />
    </div>
  );
}